import { memo } from "react"
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu"
import { Link } from "@tanstack/react-router"
import { IconBoxArchiveOutline18 as ArchiveIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { AgentSidebarRow } from "./assistant-chats"
import { hrefLinkOptions } from "@/lib/href-link"
import {
	INBOX_NAV_GROUP,
	INBOX_NAV_ROW,
	INBOX_NAV_ROW_ACTIVE,
	INBOX_NAV_ROW_INACTIVE,
} from "../inbox/components/inbox-nav-styles"
import { FADE_OUT_LABEL } from "./idea-list"

// Claude and ChatGPT show a handful of recent chats and fold the rest.
export const AGENT_SIDEBAR_LIMIT = 5

/** The rows on screen, which are the ones Tab can reach. */
export function visibleAgentRows<T>(
	rows: readonly T[],
	expanded: boolean,
): T[] {
	return expanded ? [...rows] : rows.slice(0, AGENT_SIDEBAR_LIMIT)
}

/** The stored chat a URL opens, from its ?session=. */
export function activeRowId(pathname: string, search: string): string | null {
	if (pathname !== "/agent/chat") return null
	return new URLSearchParams(search).get("session")
}

const MENU_ITEM =
	"relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium outline-none focus:bg-gray-100 dark:focus:bg-gray-800"

export const AgentIdeaRows = memo(
	({
		rows,
		activeId,
		expanded,
		onToggleExpanded,
		onArchive,
	}: {
		rows: readonly AgentSidebarRow[]
		activeId: string | null
		expanded: boolean
		onToggleExpanded: () => void
		onArchive: (chatId: string) => void
	}) => {
		const shown = visibleAgentRows(rows, expanded)
		const hidden = rows.length - AGENT_SIDEBAR_LIMIT
		return (
			<div className={cn(INBOX_NAV_GROUP, "pb-0.5")}>
				{shown.map((row) => {
					const active = row.id === activeId
					const link = (
						<Link
							{...hrefLinkOptions(row.href)}
							data-testid="agent-sidebar-idea"
							data-chat={row.chatId ? "true" : undefined}
							aria-current={active ? "page" : undefined}
							className={cn(
								INBOX_NAV_ROW,
								active ? INBOX_NAV_ROW_ACTIVE : INBOX_NAV_ROW_INACTIVE,
							)}
						>
							<span className={FADE_OUT_LABEL}>{row.title}</span>
						</Link>
					)
					const { chatId } = row
					if (!chatId) return <div key={row.id}>{link}</div>
					return (
						<ContextMenuPrimitive.Root key={row.id}>
							<ContextMenuPrimitive.Trigger asChild>
								<div>{link}</div>
							</ContextMenuPrimitive.Trigger>
							<ContextMenuPrimitive.Portal>
								<ContextMenuPrimitive.Content className="z-50 min-w-[160px] rounded-lg bg-white p-1 shadow-lg ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
									<ContextMenuPrimitive.Item
										className={MENU_ITEM}
										data-testid="agent-sidebar-archive"
										onSelect={() => onArchive(chatId)}
									>
										<ArchiveIcon className="size-4 flex-none text-tertiary" />
										Archive
									</ContextMenuPrimitive.Item>
								</ContextMenuPrimitive.Content>
							</ContextMenuPrimitive.Portal>
						</ContextMenuPrimitive.Root>
					)
				})}
				{hidden > 0 && (
					<button
						type="button"
						data-testid="agent-sidebar-more"
						onClick={onToggleExpanded}
						className={cn(
							INBOX_NAV_ROW,
							INBOX_NAV_ROW_INACTIVE,
							"cursor-default",
						)}
					>
						<span className="truncate text-gray-450 dark:text-gray-500">
							{expanded ? "View less" : "View more"}
						</span>
					</button>
				)}
			</div>
		)
	},
)
